import React, { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Box,
  Button,
  EmptyState,
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorEmpty,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorTrigger,
  Icon,
  Separator,
  Tabs,
  TabsList,
  TabsTab,
  Text,
  type EntitySelection,
  type IconName,
} from "@salestrak/ui";

import { CustomFieldFormDialog } from "#app/components/custom-fields/custom-field-form-dialog";
import { DisabledReason } from "#app/components/menus/disabled-reason";
import { getFieldTypeMeta } from "#app/lib/constants/field-type-icons";
import type { CustomFieldType } from "#app/lib/data";
import { useCustomFieldMutations } from "#app/lib/hooks/use-custom-field-mutations";
import {
  FIELD_TYPE_POOL_ID,
  fieldTypeUnavailableReason,
  useFieldTypeOrder,
  useFieldTypeSelectorConfig,
} from "#app/lib/hooks/use-field-type-order";
import { usePermission } from "#app/lib/hooks/use-permission";
import { FieldsSearch, SectionHeader } from "./display-field-rows";
import { useFieldsPageStep, type FieldsPickerTab } from "./options-panel-slots";

// ---------------------------------------------------------------------------
// The Fields page: the picker frame and the create form it opens
// ---------------------------------------------------------------------------
//
// The Fields page IS a picker: one search over two tabs. "Add existing" —
// the default, first in the strip — is the view's own roster: every field it
// can show, with its switch, shown ones first, which the editor that owns
// the view renders and passes in as children. "Create new" lists the field
// TYPES — the curated most-used few first, then all of them by name — and
// picking one drills into the create form for that type. What exists leads
// what could: a view mostly needs a field it already has shown, so the
// roster is the door and creating is the second tab. A query searches both
// tabs at once, so the strip gives way to grouped results — the roster's
// rows first, the types to create under them; nothing on either side is one
// quiet empty state.
//
// The picker owns nothing about the view. The editors own the roster and
// what showing means, and a created field is theirs to show once it exists.
// ---------------------------------------------------------------------------

/** How many of the curated type order count as "Popular". */
const POPULAR_TYPE_COUNT = 6;

export type FieldsPickerProps = {
  /** The one query, owned by the editor: the roster it passes as children is
   *  already filtered by it, and the picker filters the field types by it. */
  query: string;
  onQueryChange: (query: string) => void;
  /** The tab to open on. Absent, Add existing — the roster, which is also
   *  the whole page when the viewer cannot create. */
  initialTab?: FieldsPickerTab | undefined;
  /** Whether the roster has any row left under the query — what decides
   *  between showing it and the one empty state. */
  rosterMatches: boolean;
  /** Open the create form for a type. */
  onCreateType: (type: CustomFieldType) => void;
  /** The roster: the view's fields with their switches, in flow. */
  children: ReactNode;
};

/**
 * A pressable pick row on the Fields page rung: the 16px leading glyph slot
 * and the label, no control — the roster rows' shape without their switch.
 *
 * A row with a `disabledReason` stays in the list and says why in a tooltip
 * (the disabled-row house rule). It is `aria-disabled`, not `disabled`: a
 * disabled button receives no pointer events, so the tooltip could never
 * open on it.
 */
function PickRow({
  label,
  icon,
  iconColor,
  disabledReason,
  onClick,
}: {
  label: string;
  icon: IconName;
  iconColor?: React.ComponentProps<typeof Icon>["color"];
  disabledReason?: string | null;
  onClick: () => void;
}) {
  const disabled = Boolean(disabledReason);
  const row = (
    <Box<"button">
      as="button"
      direction="row"
      align="center"
      gap="sm"
      minHeight={32}
      paddingX="sm"
      rounded="sm"
      fill="width"
      hoverBackground={!disabled}
      opacity={disabled ? 50 : undefined}
      aria-disabled={disabled || undefined}
      onClick={disabled ? undefined : onClick}
    >
      <Box width={16} align="center" justify="center" flex="none">
        <Icon name={icon} size="md" color={iconColor ?? "muted"} />
      </Box>
      <Box direction="column" shrink fill="flex" minWidth={0}>
        <Text size="sm" truncate={1}>
          {label}
        </Text>
      </Box>
    </Box>
  );
  return disabledReason ? (
    <DisabledReason reason={disabledReason}>
      {row}
    </DisabledReason>
  ) : (
    row
  );
}

/** A titled group of pick rows on the roster's section rhythm. */
function PickSection({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <Box
      direction="column"
      paddingX="md"
      paddingY="sm"
      gap="2xs"
      role="list"
      aria-label={label}
    >
      <SectionHeader label={label} />
      {children}
    </Box>
  );
}

function TypeRows({
  types,
  onPick,
}: {
  types: string[];
  onPick: (type: CustomFieldType) => void;
}) {
  return types.map((type) => {
    const meta = getFieldTypeMeta(type);
    return (
      <PickRow
        key={type}
        label={meta.label}
        icon={meta.icon}
        iconColor={meta.color}
        disabledReason={fieldTypeUnavailableReason(type)}
        onClick={() => onPick(type as CustomFieldType)}
      />
    );
  });
}

export function FieldsPicker({
  query,
  onQueryChange,
  initialTab,
  rosterMatches,
  onCreateType,
  children,
}: FieldsPickerProps) {
  const canManage = usePermission("custom_fields.manage");
  const [tab, setTab] = useState<FieldsPickerTab>(initialTab ?? "existing");
  // Without the manage grant there is nothing to create: the roster is the
  // whole page, whatever tab was asked for.
  const creating = canManage && tab === "create";
  const typeOrder = useFieldTypeOrder();

  const q = query.trim().toLowerCase();
  const searching = q.length > 0;

  // The curated order leads with the most-used types; "All" is the same set
  // by name, for a reader who knows what they want and not where it ranks.
  const popularTypes = typeOrder.slice(0, POPULAR_TYPE_COUNT);
  const allTypes = useMemo(
    () =>
      [...typeOrder].sort((a, b) =>
        getFieldTypeMeta(a).label.localeCompare(getFieldTypeMeta(b).label),
      ),
    [typeOrder],
  );
  const matchingTypes = canManage
    ? allTypes.filter((type) =>
        getFieldTypeMeta(type).label.toLowerCase().includes(q),
      )
    : [];

  let body: ReactNode;
  if (searching) {
    body =
      matchingTypes.length === 0 && !rosterMatches ? (
        <EmptyState
          size="sm"
          media="circle"
          icon="search"
          title="No matching fields"
        />
      ) : (
        <>
          {rosterMatches ? children : null}
          {matchingTypes.length > 0 && rosterMatches ? <Separator /> : null}
          {matchingTypes.length > 0 ? (
            <PickSection label="Create new">
              <TypeRows types={matchingTypes} onPick={onCreateType} />
            </PickSection>
          ) : null}
        </>
      );
  } else if (creating) {
    body = (
      <>
        <PickSection label="Popular">
          <TypeRows types={popularTypes} onPick={onCreateType} />
        </PickSection>
        <Separator />
        <PickSection label="All">
          <TypeRows types={allTypes} onPick={onCreateType} />
        </PickSection>
      </>
    );
  } else {
    body = children;
  }

  return (
    <>
      {/* The tabs ride the search rung, under the well and flush on the
          hairline the active tab's bar overlaps. A query searches both tabs
          at once, so while one is typed the strip has nothing to pick and
          gives way; without the manage grant there is one tab, so no strip. */}
      <FieldsSearch
        value={query}
        onValueChange={onQueryChange}
        placeholder="Search fields"
      >
        {!searching && canManage ? (
          <Box paddingX="md" paddingTop="xs">
            <Tabs
              value={tab}
              onValueChange={(next) => setTab(next as FieldsPickerTab)}
            >
              <TabsList variant="underline">
                <TabsTab value="existing">Add existing</TabsTab>
                <TabsTab value="create">Create new</TabsTab>
              </TabsList>
            </Tabs>
          </Box>
        ) : null}
      </FieldsSearch>
      {/* The first section's own 8px top inset plus this 4px matches the
          12px between the search and the hairline above. */}
      <Box direction="column" paddingTop="xs">
        {body}
      </Box>
    </>
  );
}

// ---------------------------------------------------------------------------
// The create form as a page
// ---------------------------------------------------------------------------

export type CreateFieldPageProps = {
  type: CustomFieldType;
  /** The form's Cancel — back to the picker. */
  onCancel: () => void;
  /** The field exists; the editor shows it and returns to the roster. */
  onCreated: (customFieldId: string) => void;
};

/**
 * The Custom Field Manager's create form seated as a Fields page: the shell
 * draws the header (Back, the type as a switchable title) and scrolls, the
 * form renders in flow. Creating runs the same mutation the manager runs —
 * the list query it refetches is the one every Fields editor reads its
 * fields from, so the new field lands in the roster on its own.
 */
export function CreateFieldPage({ type, onCancel, onCreated }: CreateFieldPageProps) {
  const { createCustomField, updateCustomField } = useCustomFieldMutations();
  // The form calls onClose after a successful create as well as on Cancel;
  // a create already reported where to go, so that close is not a cancel.
  const createdRef = useRef(false);
  const formMode = useMemo(() => ({ mode: "create" as const, type }), [type]);
  // The shell guards a close or a Back against a form with something typed
  // in it; the form says when that is so, and says it is not as it leaves.
  const reportDirty = useFieldsPageStep()?.reportDirty;
  useEffect(() => () => reportDirty?.(false), [reportDirty]);
  return (
    <CustomFieldFormDialog
      presentation="page"
      formMode={formMode}
      onDirtyChange={reportDirty}
      onClose={() => {
        if (!createdRef.current) onCancel();
      }}
      onCreate={async (input) => {
        const created = await createCustomField(input);
        if (created && "customFieldId" in created) {
          createdRef.current = true;
          onCreated(created.customFieldId);
        }
        return created;
      }}
      onUpdate={updateCustomField}
    />
  );
}

// ---------------------------------------------------------------------------
// The create page's title
// ---------------------------------------------------------------------------

export type FieldTypeTitleProps = {
  type: CustomFieldType;
  onTypeChange: (type: CustomFieldType) => void;
};

/**
 * The create page is titled by its TYPE, and the title is the type switcher
 * — the manager's own header grammar, seated in the shell's title slot. A
 * ghost trigger, pulled back by its own inset so the label sits where a
 * plain title would.
 */
export function FieldTypeTitle({ type, onTypeChange }: FieldTypeTitleProps) {
  const config = useFieldTypeSelectorConfig();
  const meta = getFieldTypeMeta(type);
  return (
    <Box marginLeftNegative="sm">
      <EntitySelector
        config={config}
        value={[]}
        onValueChange={(selections: EntitySelection[]) => {
          const selected = selections[0];
          if (selected) onTypeChange(selected.id as CustomFieldType);
        }}
      >
        <EntitySelectorTrigger
          render={
            <Button variant="ghost" size="sm" aria-label="Change field type">
              <Text size="base" weight="semibold">
                {meta.label}
              </Text>
              <Icon name="chevron-down" size="sm" color="muted" />
            </Button>
          }
        />
        <EntitySelectorContent align="start">
          <EntitySelectorInput />
          <EntitySelectorPool value={FIELD_TYPE_POOL_ID} />
          <EntitySelectorEmpty>No matching field types</EntitySelectorEmpty>
        </EntitySelectorContent>
      </EntitySelector>
    </Box>
  );
}
